import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './HowItWorks.css';
import { PLATFORM } from './howItWorksContent';

// "How It Works" trigger plus a panel that slides up from the bottom.
// Open/close, backdrop, Escape and focus return follow the Web Apps panel.
// `content` is one entry from HOW_IT_WORKS: { name, plain, technical }.
export default function HowItWorks({ content }) {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);
  const onClose = () => setIsOpen(false);

  // Focus the panel when it opens so Escape key works; return focus on close
  useEffect(() => {
    if (isOpen && panelRef.current) {
      returnFocusRef.current = document.activeElement;
      panelRef.current.focus();
    } else if (!isOpen && returnFocusRef.current) {
      returnFocusRef.current.focus?.();
      returnFocusRef.current = null;
    }
  }, [isOpen]);

  // Keys pressed in the panel stay in the panel, so the page's own window
  // shortcuts (ARTyping cell edits, the Converter's Escape to cancel) don't fire
  const onKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Escape') onClose();
  };

  return (
    <>
      <button type="button" className="hiw-trigger" onClick={() => setIsOpen(true)}
        aria-haspopup="dialog" aria-expanded={isOpen}>
        How It Works <span className="kutz-icon hiw-trigger__icon" aria-hidden="true">W</span>
      </button>

      {/* Portalled so a page's own layout can't clip or offset the fixed panel */}
      {createPortal(
        <>
          {/* Backdrop */}
          <div
            className={`hiw-backdrop ${isOpen ? 'hiw-backdrop--visible' : ''}`}
            onClick={onClose}
          />

          {/* Panel */}
          <aside
            ref={panelRef}
            className={`hiw-panel ${isOpen ? 'hiw-panel--open' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-label={`${content.name}: How It Works`}
            aria-hidden={!isOpen}
            tabIndex={-1}
            onKeyDown={onKeyDown}
          >
            <div className="hiw-paper">

              {/* Close button */}
              <button className="hiw-close" onClick={onClose} title="Close">✕</button>

              <div className="hiw-sheet">

                {/* Masthead */}
                <div className="hiw-masthead">
                  <p className="hiw-masthead__kicker">{content.name}</p>
                  <div className="hiw-rule hiw-rule--double" />
                  <h2 className="hiw-masthead__title">How It Works</h2>
                  <div className="hiw-rule hiw-rule--double" />
                </div>

                <section className="hiw-section">
                  <h3 className="hiw-section__label">In Plain Terms</h3>
                  {content.plain.map((p, i) => <p className="hiw-plain" key={i}>{p}</p>)}
                </section>

                <div className="hiw-rule hiw-rule--thin" />

                <section className="hiw-section">
                  <h3 className="hiw-section__label">Under the Hood</h3>
                  <ul className="hiw-list">
                    {content.technical.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </section>

                <div className="hiw-rule hiw-rule--double" />

                <section className="hiw-section hiw-section--platform">
                  <h3 className="hiw-section__label">About the Platform</h3>
                  {PLATFORM.plain.map((p, i) => <p className="hiw-plain hiw-plain--small" key={i}>{p}</p>)}
                  <ul className="hiw-list hiw-list--small">
                    {PLATFORM.technical.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                </section>

              </div>
            </div>
          </aside>
        </>,
        document.body
      )}
    </>
  );
}
